import { Medication } from 'src/app/models/fhir-models';
import { MedicationFrequencyOption } from './fhir-medications.model';

/**
 * BRD-CLIN-2026-006 §4.2 — is this medication record at or above its loop diuretic ceiling?
 * =========================================================================================
 * PURE. No Angular, no DI, mirroring `medication-hours.util.ts` so the component and the service can
 * both consume it. The arithmetic that decides whether a nurse is warned is testable against plain
 * objects rather than through a component harness.
 *
 * ## THE SECOND NET, NOT THE FIRST
 *
 * The Care Call Guide already checks the ceiling on the call, both against the current dose (#124)
 * and against the dose a doubling would produce. This checks the MEDICATION RECORD, which is the
 * surface that sees a dose entered outside a call — a reconciliation, an EHR import, a correction
 * typed between calls. It detects and warns; it raises no alert. `CreateCareCallAlertsRequest`
 * requires `guide_diagnosis` and `guide_version`, which a medication save has neither of, and faking
 * a guide version to reuse that writer would put a fabricated provenance on a clinical alert.
 *
 * ## UNKNOWN IS NOT UNDER-CEILING, AND THAT IS THE WHOLE CONTRACT
 *
 * `mgPerDay: null` means the daily dose could not be established — a free-text frequency, a missing
 * amount, a unit this cannot convert. It must NEVER be read as "below the ceiling". A record whose
 * dose cannot be computed is INCOMPLETE, and the right response is to get the number, not to assume
 * the patient is safe. Callers get a `reason` so they can say which.
 *
 * That is why this returns a discriminated result rather than a boolean: a boolean has no room for
 * "I could not tell", and every place that has to squeeze one in ends up defaulting to false.
 *
 * ## WHY A CURATED LIST AND NOT RxNorm
 *
 * Three drugs are in scope. The form's autocomplete calls RxNav (`fhir-medications.service.ts:29`)
 * but captures display names only — `medref` is hardcoded 'N/A'
 * (`fhir-medication-detail.component.ts:465`) — so there is no RxCUI to match on and a curated
 * synonym list is both sufficient and more predictable. Brand names are first-class: they are what is
 * printed on the bottle and what a patient reads out.
 */

/**
 * Everything the ceiling check needs, resolved ONCE by the medications list and handed to the detail
 * dialog.
 *
 * The list has already resolved the gate — the clinic's CHF care-call toggle AND the patient's
 * diagnoses resolving to CHF — and read both config documents. Passing the result means the dialog
 * does not repeat two Firestore reads and a diagnosis resolution every time it opens, and cannot
 * disagree with the row the user clicked to get there.
 */
export interface MedicationCeilingContext {
  drugs: LoopDiureticConfigEntry[];
  frequencies: MedicationFrequencyOption[];
}

/** One drug from `welby_configuration/loop_diuretics`. Seeded by welby-firestore's SeedLoopDiuretics. */
export interface LoopDiureticConfigEntry {
  ingredient: string;
  synonyms: string[];
  ceiling_mg_day: number;
}

/** Why a daily dose could not be established. Each maps to something a human can fix.
 *
 * `no_ceiling` IS THE ONE A CLINICIAN CANNOT FIX, and it is called out here because the rule above
 * otherwise reads as absolute. The other four are answered by completing the record on screen; this
 * one means `welby_configuration/loop_diuretics` holds no usable `ceiling_mg_day` for the drug, which
 * takes an engineer. Both surfaces give it its own wording for that reason — telling an MA to
 * complete a dose that is already filled in sends them looking for a problem that is not there.
 */
export type LoopDiureticUnknownReason =
  /** The frequency is "Other" or "As needed" — no multiplier exists, by design. */
  | 'free_text_frequency'
  /** The frequency is absent, or not one the configuration knows. */
  | 'no_frequency'
  /** `dosageAmount` is absent, blank, or not a usable number. */
  | 'no_amount'
  /** The unit is not one this can convert to mg — notably mcg, for these drugs. */
  | 'unit_unconvertible'
  /** The configured `ceiling_mg_day` is absent or unusable — an ENGINEER's fix, not a clinician's. */
  | 'no_ceiling';

export interface LoopDiureticDose {
  ingredient: string;
  /** The total daily dose in mg, or `null` when it could not be established. */
  mgPerDay: number | null;
  /** Present only alongside a computed `mgPerDay`. */
  ceilingMgDay?: number;
  atOrAbove?: boolean;
  /** Present only when `mgPerDay` is null. */
  reason?: LoopDiureticUnknownReason;
}

/*
 * A FLAT SHAPE rather than a discriminated union, for the reason `diuretic-screen.util.ts` records:
 * this project does not enable `strict`, so narrowing on a literal tag is not dependable at the call
 * site and every consumer ends up casting. The two helper predicates below are the supported way to
 * ask the two questions, so no caller has to reach for `mgPerDay !== null` itself.
 */

/**
 * Units this converts to mg, and their multiplier.
 *
 * `mcg` IS DELIBERATELY ABSENT. It is convertible arithmetically — 1000 mcg is 1 mg — but no loop
 * diuretic in scope is dosed in micrograms, so a record saying "40 mcg furosemide" is a data error,
 * not a microgram prescription. Dividing by 1000 would silently turn it into 0.04 mg/day and report
 * a patient as comfortably under a ceiling on the strength of a typo. It resolves to
 * `unit_unconvertible`, which marks the record incomplete and gets a human to look.
 */
const MG_MULTIPLIER: Readonly<Record<string, number>> = { mg: 1, g: 1000 };

/** Lowercased, trimmed. `dosageMeasure` arrives from a free-typed form field in mixed case. */
const normaliseUnit = (raw: unknown): string => (typeof raw === 'string' ? raw.trim().toLowerCase() : '');

/**
 * A number, or null — with the same rejection rules `escalation-stamp.ts` applies.
 *
 * `''`, `null`, `undefined`, booleans and unparseable strings are NULL, never 0. A dose of zero is a
 * meaningful clinical statement ("they are not taking it") and must not be manufactured out of an
 * empty field; and zero would compare as under every ceiling, which is the wrong answer for a record
 * nobody filled in.
 */
const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/** The configured drug whose synonyms match this medication name, or null. */
export const matchLoopDiuretic = (
  name: unknown,
  config: LoopDiureticConfigEntry[] | null | undefined,
): LoopDiureticConfigEntry | null => {
  if (typeof name !== 'string' || !Array.isArray(config)) {
    return null;
  }
  const haystack = name.trim().toLowerCase();
  if (!haystack) {
    return null;
  }
  // WORD-BOUNDARY, not `includes`. A medication named "Furosemide-sparing regimen note" should match
  // furosemide, but a substring test would also match a drug whose name merely contains a synonym as
  // a fragment. Splitting on non-letters keeps "Lasix 40mg" and "furosemide/HCTZ" matching while a
  // token like "nonlasixish" does not.
  const tokens = new Set(haystack.split(/[^a-z]+/).filter(Boolean));
  return config.find((drug) => (drug.synonyms || []).some((s) => tokens.has(s))) ?? null;
};

/**
 * The medication's total daily dose against its ceiling, or why it could not be computed.
 *
 * Returns `null` when the drug is not a loop diuretic in scope — that is not an unknown, it is a
 * question that does not apply, and a caller must not warn about it.
 */
export const resolveLoopDiureticDose = (
  med: Medication | null | undefined,
  config: LoopDiureticConfigEntry[] | null | undefined,
  frequencies: MedicationFrequencyOption[] | null | undefined,
): LoopDiureticDose | null => {
  const drug = matchLoopDiuretic(med?.name, config);
  if (!drug) {
    return null;
  }
  const ingredient = drug.ingredient;

  const amount = asNumber(med?.dosageAmount);
  if (amount === null || amount < 0) {
    return { ingredient, mgPerDay: null, reason: 'no_amount' };
  }

  const unit = normaliseUnit(med?.dosageMeasure);
  // `hasOwnProperty.call`, not a bare lookup. `MG_MULTIPLIER` is an object literal, so it inherits
  // `Object.prototype`: `normaliseUnit` lowercases, which neutralises `toString`/`valueOf`, but
  // `constructor` is already lowercase and returned the Object constructor. The arithmetic below then
  // produced NaN, which satisfied NEITHER predicate — no warning icon and no "could not be
  // calculated" either, the one outcome this module must never reach. `frequencyTimesPerDay.js` in
  // the paired welby-firestore PR guards the same way.
  const multiplier = Object.prototype.hasOwnProperty.call(MG_MULTIPLIER, unit) ? MG_MULTIPLIER[unit] : undefined;
  if (multiplier === undefined) {
    return { ingredient, mgPerDay: null, reason: 'unit_unconvertible' };
  }

  const option = (frequencies || []).find((f) => f && f.value === med?.frequency);
  if (!option) {
    return { ingredient, mgPerDay: null, reason: 'no_frequency' };
  }
  // ONE CHECK, not two. `timesPerDay: null` is the configuration SAYING it cannot be determined —
  // "Other", "As needed", "Weekly" — and `asNumber` already returns null for that, for `undefined`
  // on an unseeded document, and for anything unusable. An explicit `=== null` guard ahead of this
  // produced the same reason on the same inputs and was therefore an untestable branch: removing it
  // left the whole suite green, which is how it was found.
  //
  // Separate from `no_frequency` because the fix differs: this needs the clinician to pick a concrete
  // schedule, that needs the frequency filled in at all.
  const timesPerDay = asNumber(option.timesPerDay);
  if (timesPerDay === null || timesPerDay <= 0) {
    return { ingredient, mgPerDay: null, reason: 'free_text_frequency' };
  }

  // THE CEILING IS VALIDATED LIKE EVERY OTHER INPUT, raised in review. It was the one value that came
  // straight from Firestore unguarded, and both failure modes were the ones this module exists to
  // prevent: an ABSENT ceiling made `mgPerDay >= undefined` false, so 200 mg/day of furosemide
  // reported as comfortably under and showed no icon at all — a fail-OPEN; and a `null` ceiling
  // coerced to `>= 0`, so everything was "at or above" and the hint rendered "the null mg/day
  // ceiling", the same class of defect as the `undefined` label that reached the §5.1 billing record.
  //
  // The seed validator in the paired welby-firestore PR does enforce a positive finite number, but it
  // guards the WRITE and this is the READ. A console edit, a partial seed or a future schema change
  // all reach here without passing it.
  const ceilingMgDay = asNumber(drug.ceiling_mg_day);
  if (ceilingMgDay === null || ceilingMgDay <= 0) {
    return { ingredient, mgPerDay: null, reason: 'no_ceiling' };
  }

  const mgPerDay = amount * multiplier * timesPerDay;
  return {
    ingredient,
    mgPerDay,
    ceilingMgDay,
    // AT or above, matching the guide's `at_or_above` bound and §4.2's "ceiling dose reached". The
    // ceiling is a value you may reach and not pass.
    atOrAbove: mgPerDay >= ceilingMgDay,
  };
};

/** True only when the dose is KNOWN and at or above the ceiling. Unknown is never a warning of this kind. */
export const isAtOrAboveCeiling = (dose: LoopDiureticDose | null | undefined): boolean =>
  !!dose && Number.isFinite(dose.mgPerDay as number) && dose.atOrAbove === true;

/** True when this is a loop diuretic whose dose could not be established — an INCOMPLETE record. */
export const isDoseUnknown = (dose: LoopDiureticDose | null | undefined): boolean =>
  // `!Number.isFinite`, not `=== null`. The null check was the whole net, so any NaN that reached
  // here escaped BOTH predicates and the row rendered no icon in either state. Nothing should produce
  // a NaN now that the unit lookup and the ceiling are guarded — which is exactly why this is the
  // backstop rather than the fix.
  !!dose && !Number.isFinite(dose.mgPerDay as number);
